import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, Eye, EyeOff } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { supabase } from '../../lib/supabase';
import { confirmRedirect } from '../../lib/signupCompletion';
import { clearAuthLinkError } from '../../lib/authLinkError';

const glassCard = {
  background: 'rgba(255, 255, 255, 0.12)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
};

const bgStyle = {
  background: `
    radial-gradient(ellipse at 20% 50%, var(--gradient-warm-1) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 20%, var(--gradient-warm-2) 0%, transparent 40%),
    radial-gradient(ellipse at 50% 80%, var(--gradient-warm-3) 0%, transparent 45%),
    radial-gradient(ellipse at 70% 60%, var(--gradient-warm-4) 0%, transparent 50%),
    var(--gradient-base)
  `,
  backgroundSize: '200% 200%',
  animation: 'splashGradientShift 12s ease infinite',
};

const SignInScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, session, loading } = useUser();
  // Prefilled when onboarding's check-your-email step sends them here.
  const [email, setEmail] = useState(() => location.state?.email ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Email confirmation: signed up but never tapped the link.
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent | error
  // Arrived from an expired / already-used email link (src/lib/authLinkError.js).
  const [linkError] = useState(() => !!location.state?.linkError);
  useEffect(() => { if (linkError) clearAuthLinkError(); }, [linkError]);

  useEffect(() => {
    if (!loading && session) {
      navigate('/feed', { replace: true });
    }
  }, [session, loading, navigate]);

  const handleSignIn = async () => {
    if (!email.trim() || !password) return;
    setError('');
    setUnconfirmed(false);
    setSubmitting(true);
    const { error } = await signIn(email.trim(), password);
    setSubmitting(false);
    if (error) {
      // Supabase only says this after the password checked out.
      if (error.code === 'email_not_confirmed' || /not confirmed/i.test(error.message || '')) {
        setUnconfirmed(true);
        setResendState('idle');
        setError('Please confirm your email first. Tap the link we sent you, then sign in.');
      } else {
        setError('Incorrect email or password. Please try again.');
      }
    } else {
      navigate('/feed', { replace: true });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSignIn();
  };

  return (
    <div className="min-h-screen flex flex-col px-6 pb-4" style={{ ...bgStyle, paddingTop: 'calc(var(--sat) + 1rem)' }}>
      {/* Back button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/')}
          className="w-9 h-9 rounded-full flex items-center justify-center transition-all active:scale-95"
          style={{
            backgroundColor: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.15)',
          }}
        >
          <ChevronLeft size={20} className="text-white" />
        </button>
      </div>

      <div className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full splash-entrance">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Welcome back</h1>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Sign in to your DSP Cafe account
          </p>
        </div>

        <div className="rounded-2xl p-6 space-y-4 mb-4" style={glassCard}>
          {/* Email */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="email"
              className="w-full px-4 py-3 rounded-xl text-base focus:ring-2 focus:outline-none"
              style={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff',
              }}
              placeholder="you@example.com"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                autoComplete="current-password"
                className="w-full px-4 py-3 pr-12 rounded-xl text-base focus:ring-2 focus:outline-none"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#fff',
                }}
                placeholder="Password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 transition-all active:scale-95"
                style={{ color: 'rgba(255,255,255,0.5)' }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Forgot password */}
          <div className="text-right -mt-1">
            <button
              onClick={() => navigate('/forgot-password')}
              className="text-xs transition-all"
              style={{ color: 'rgba(255,255,255,0.55)' }}
            >
              Forgot password?
            </button>
          </div>

          {linkError && !error && (
            <p className="text-sm text-center rounded-xl p-3" style={{ color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' }} role="status">
              That link has expired or was already used. If you were confirming your email, it&rsquo;s most likely
              confirmed already, so try signing in below. If it isn&rsquo;t, we&rsquo;ll offer to send a new link.
            </p>
          )}

          {/* Error */}
          {error && (
            <p className="text-sm text-center" style={{ color: '#F87171' }}>
              {error}
            </p>
          )}
          {unconfirmed && (
            <div className="text-center">
              <button
                type="button"
                onClick={async () => {
                  setResendState('sending');
                  const { error: err } = await supabase.auth.resend({
                    type: 'signup',
                    email: email.trim(),
                    options: { emailRedirectTo: confirmRedirect() },
                  });
                  setResendState(err ? 'error' : 'sent');
                }}
                disabled={resendState === 'sending' || resendState === 'sent'}
                className="text-sm font-semibold underline disabled:opacity-60"
                style={{ color: 'rgba(255,255,255,0.9)' }}
              >
                {resendState === 'sending' ? 'Sending…' : resendState === 'sent' ? 'Sent. Check your email' : 'Send the confirmation email again'}
              </button>
              {resendState === 'error' && (
                <p className="text-xs mt-1" style={{ color: '#FCA5A5' }}>That didn&rsquo;t send. Wait a minute and try again.</p>
              )}
            </div>
          )}
        </div>

        <button
          onClick={handleSignIn}
          disabled={submitting || !email.trim() || !password}
          className="w-full py-3.5 rounded-xl font-semibold text-white text-base mb-4 transition-all duration-200 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:hover:brightness-100 flex items-center justify-center gap-2"
          style={{ backgroundColor: 'var(--brand-primary)' }}
        >
          {submitting ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </button>

        <p className="text-center text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
          New to DSP Cafe?{' '}
          <button
            onClick={() => navigate('/onboarding')}
            className="font-semibold underline transition-all"
            style={{ color: 'rgba(255,255,255,0.9)' }}
          >
            Create an account
          </button>
        </p>
      </div>
    </div>
  );
};

export default SignInScreen;
